import React, { useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip, Box } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useThemeColors from "hooks/useThemeColors";

// Icons - same as CardIcon in each page
import SchoolIcon from "@material-ui/icons/School";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import AssignmentIcon from "@material-ui/icons/Assignment";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import DescriptionIcon from "@material-ui/icons/Description";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    padding: "4px 6px",
    borderRadius: "20px",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
  divider: {
    width: "1px",
    height: "16px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    margin: "0 4px",
  },
  iconButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    minWidth: "32px",
    minHeight: "32px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "all 0.15s ease",
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:focus": {
      outline: "none",
    },
  },
  iconActive: {
    backgroundColor: ({ clientColor }) => `${clientColor}15`,
    "&:hover": {
      backgroundColor: ({ clientColor }) => `${clientColor}25`,
    },
  },
  icon: {
    fontSize: "18px",
    color: "#999",
    transition: "color 0.15s ease",
    pointerEvents: "none",
  },
  iconActiveColor: {
    color: ({ clientColor }) => clientColor,
  },
}));

const sections = [
  // Learning
  {
    path: "/main/cts-methodology",
    key: "methodology",
    Icon: SchoolIcon,
    group: "learning",
  },
  {
    path: "/main/cts-best-practices",
    key: "best_practices",
    Icon: PlaylistAddCheckIcon,
    group: "learning",
  },
  {
    path: "/main/cts-data-request",
    key: "data_request",
    Icon: AssignmentIcon,
    group: "learning",
  },
  {
    path: "/main/cts-criteria-library",
    key: "criteria_library",
    Icon: LibraryBooksIcon,
    group: "learning",
  },
  // Building
  {
    path: "/main/cts-pl-registration",
    key: "pl_registration",
    Icon: AccountTreeIcon,
    group: "building",
  },
  {
    path: "/main/cts-data-tracking",
    key: "data_tracking",
    Icon: TrackChangesIcon,
    group: "building",
  },
  {
    path: "/main/cts-documentation",
    key: "documentation",
    Icon: DescriptionIcon,
    group: "building",
  },
];

export default function CTSMiniNav() {
  const { clientColor } = useThemeColors();
  const classes = useStyles({ clientColor });
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const currentIndex = sections.findIndex((s) => s.path === location.pathname);

  const goToSection = useCallback(
    (index) => {
      if (index >= 0 && index < sections.length) {
        navigate(sections[index].path);
      }
    },
    [navigate],
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }
      if (e.target.isContentEditable) {
        return;
      }

      if (e.key === "ArrowLeft" && currentIndex > 0) {
        e.preventDefault();
        goToSection(currentIndex - 1);
      } else if (e.key === "ArrowRight" && currentIndex < sections.length - 1) {
        e.preventDefault();
        goToSection(currentIndex + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, goToSection]);

  const getLabel = (key, group) => {
    if (group === "learning") {
      return t(`sidebar_cts.cts_learning_menu.${key}`);
    }
    return t(`sidebar_cts.cts_building_menu.${key}`);
  };

  const learningItems = sections.filter((s) => s.group === "learning");
  const buildingItems = sections.filter((s) => s.group === "building");

  const renderIcon = (section) => {
    const { Icon, key, path, group } = section;
    const isActive = path === location.pathname;
    const globalIndex = sections.findIndex((s) => s.path === path);

    return (
      <Tooltip key={key} title={getLabel(key, group)} arrow placement="bottom">
        <button
          className={`${classes.iconButton} ${isActive ? classes.iconActive : ""}`}
          onClick={() => goToSection(globalIndex)}
          aria-label={getLabel(key, group)}
        >
          <Icon
            className={`${classes.icon} ${isActive ? classes.iconActiveColor : ""}`}
          />
        </button>
      </Tooltip>
    );
  };

  return (
    <Box className={classes.container}>
      {learningItems.map(renderIcon)}
      <div className={classes.divider} />
      {buildingItems.map(renderIcon)}
    </Box>
  );
}
